import React, { useState, useEffect } from 'react';
import { Table, Button, Input, Space, Popconfirm, message, Card, Modal, Form, Upload, InputNumber } from 'antd';
import { PlusOutlined, UploadOutlined, EditOutlined, DeleteOutlined, ExclamationCircleOutlined } from '@ant-design/icons';
import { Modal as AntdModal } from 'antd';
import { trafficPoolApi } from '../../services/api';

const { TextArea } = Input;

/** 按行拆分：每一非空行 = 一个关键词，批内去重 */
function parseKeywordsFromText(text) {
  if (!text || typeof text !== 'string') return [];
  const seen = new Set();
  const result = [];
  for (const line of text.split(/\r?\n/)) {
    const kw = line.trim();
    if (!kw || seen.has(kw)) continue;
    seen.add(kw);
    result.push(kw);
  }
  return result;
}

const TrafficPoolList = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form] = Form.useForm();
  const [uploading, setUploading] = useState(false);

  const isEdit = Boolean(editing?.id);

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await trafficPoolApi.getPools();
      setData(res.items || res.data || res);
    } catch {
      message.error('获取流量池数据失败');
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (!editing) return;
    if (editing.id) {
      form.setFieldsValue({
        name: editing.name,
        traffic_value: editing.traffic_value ?? 0,
        competition: editing.competition ?? 0,
        category: editing.category ?? '',
      });
    } else {
      form.setFieldsValue({
        name: '',
        traffic_value: 0,
        competition: 0,
        category: '',
      });
    }
  }, [editing, form]);

  const handleAdd = () => {
    setEditing({});
  };

  const handleEdit = (record) => {
    setEditing(record);
  };

  const handleDelete = async (id) => {
    try {
      await trafficPoolApi.deletePool(id);
      message.success('删除成功');
      fetchData();
    } catch {
      message.error('删除失败');
    }
  };

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);
      if (isEdit) {
        await trafficPoolApi.updatePool(editing.id, {
          ...values,
          name: String(values.name).trim(),
        });
        message.success('修改成功');
      } else {
        const names = parseKeywordsFromText(values.name);
        if (names.length === 0) {
          message.error('请至少输入一个关键词（每行一个）');
          return;
        }
        const res = await trafficPoolApi.createPoolsBatch({
          names,
          traffic_value: values.traffic_value,
          competition: values.competition,
          category: values.category || '',
        });
        message.success(res.message || `成功添加 ${res.created} 个关键词`);
      }
      setEditing(null);
      fetchData();
    } catch (err) {
      if (err?.errorFields) return;
      message.error(err.response?.data?.detail || '保存失败');
    } finally {
      setSaving(false);
    }
  };

  const handleUpload = async (options) => {
    setUploading(true);
    const { file, onSuccess, onError } = options;
    try {
      const res = await trafficPoolApi.importPools(file);
      if (res && res.message) {
        Modal.info({ title: '导入结果', content: <div dangerouslySetInnerHTML={{__html: res.message}} /> });
      }
      fetchData();
      onSuccess && onSuccess();
    } catch (err) {
      console.error('导入失败:', err);
      let msg = '导入失败';
      if (err.response && err.response.data && err.response.data.message) {
        msg = err.response.data.message;
      }
      Modal.error({ title: '导入失败', content: msg });
      onError && onError(err);
    }
    setUploading(false);
  };

  const handleModalCancel = () => {
    setEditing(null);
  };

  const handleDeleteAll = () => {
    AntdModal.confirm({
      title: '确定要删除所有流量池吗？',
      icon: <ExclamationCircleOutlined />,
      content: '此操作不可恢复，是否继续？',
      okText: '确定',
      cancelText: '取消',
      onOk: async () => {
        try {
          const res = await trafficPoolApi.deleteAllPools();
          message.success(res.message || '已删除所有流量池');
          fetchData();
        } catch (err) {
          console.error('删除失败:', err);
          message.error(err.response?.data?.message || '删除失败');
        }
      },
    });
  };

  const columns = [
    { title: '关键词', dataIndex: 'name', key: 'name' },
    { title: '流量值', dataIndex: 'traffic_value', key: 'traffic_value' },
    { title: '竞争度', dataIndex: 'competition', key: 'competition' },
    { title: '类别', dataIndex: 'category', key: 'category' },
    {
      title: '操作',
      key: 'action',
      render: (_, record) => (
        <Space>
          <Button type="link" icon={<EditOutlined />} onClick={() => handleEdit(record)}>编辑</Button>
          <Popconfirm title="确定删除？" onConfirm={() => handleDelete(record.id)}>
            <Button type="link" danger icon={<DeleteOutlined />}>删除</Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <Card title="流量池管理">
        <Space style={{ marginBottom: 16 }}>
          <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>新增流量池</Button>
          <Upload
            showUploadList={false}
            customRequest={handleUpload}
            accept=".xls,.xlsx,.xlsm"
          >
            <Button icon={<UploadOutlined />} loading={uploading}>导入Excel</Button>
          </Upload>
          <Button danger icon={<DeleteOutlined />} onClick={handleDeleteAll}>一键全删除</Button>
        </Space>
        <Table columns={columns} dataSource={data} rowKey="id" loading={loading} />
      </Card>
      <Modal
        open={!!editing}
        title={isEdit ? '编辑流量池' : '新增流量池（批量）'}
        onCancel={handleModalCancel}
        onOk={handleSave}
        confirmLoading={saving}
        width={isEdit ? 520 : 720}
        styles={{ body: { maxHeight: '75vh', overflowY: 'auto' } }}
        destroyOnClose
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="name"
            label={isEdit ? '关键词' : '关键词（每行一个，可批量粘贴）'}
            rules={[{ required: true, message: isEdit ? '请输入关键词' : '请至少输入一行关键词' }]}
            extra={!isEdit ? '例如：第一行 Fotel，第二行 Fotel do salonu，各为独立关键词' : undefined}
          >
            {isEdit ? (
              <Input autoComplete="off" />
            ) : (
              <TextArea
                placeholder={'Fotel\nFotel do salonu\nKrzesło\n...'}
                autoSize={{ minRows: 6 }}
                autoComplete="off"
              />
            )}
          </Form.Item>
          <Form.Item
            name="traffic_value"
            label="流量值"
            rules={[{ required: true, message: '请输入流量值' }]}
          >
            <InputNumber style={{ width: '100%' }} min={0} />
          </Form.Item>
          <Form.Item
            name="competition"
            label="竞争度"
            rules={[{ required: true, message: '请输入竞争度' }]}
          >
            <InputNumber style={{ width: '100%' }} min={0} step={0.01} />
          </Form.Item>
          <Form.Item name="category" label="类别">
            <Input autoComplete="off" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default TrafficPoolList;
